import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Prompt } from '@/types/prompt';
import { useAuthStore } from '@/stores/authStore';
import { useFavoriteStore } from '@/stores/favoriteStore';
import ReactMarkdown from 'react-markdown';

interface PromptDetailProps {
  prompt: Prompt | null;
  isOpen: boolean;
  onClose: () => void;
}

export function PromptDetail({ prompt, isOpen, onClose }: PromptDetailProps) {
  const { user } = useAuthStore();
  const { favorites, addFavorite, removeFavorite } = useFavoriteStore();
  const [copied, setCopied] = useState(false);

  if (!prompt) return null;

  const isFavorited = favorites.includes(prompt.id);

  // 复制 Prompt 内容
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // 切换收藏状态
  const handleToggleFavorite = async () => {
    if (!user) {
      alert('请先登录');
      return;
    }

    try {
      if (isFavorited) {
        await removeFavorite(prompt.id);
      } else {
        await addFavorite(prompt.id);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm z-40"
          />

          {/* 模态框 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="absolute inset-4 md:inset-8 lg:inset-16 z-50 flex items-center justify-center"
          >
            <div className="w-full h-full max-w-4xl bg-background/95 backdrop-blur-xl border border-border rounded-xl md:rounded-2xl shadow-2xl flex flex-col">
              {/* 头部 */}
              <div className="flex items-start justify-between p-4 md:p-6 border-b border-border">
                <div className="flex-1 min-w-0 pr-2 md:pr-4">
                  <h2 className="text-lg md:text-2xl font-bold mb-1 md:mb-2">
                    {prompt.title}
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">{prompt.description}</p>

                  {/* 标签 */}
                  {prompt.tags && prompt.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {prompt.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 rounded-full text-xs bg-secondary text-secondary-foreground border border-border"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* 操作按钮 */}
                <div className="flex items-center gap-1 md:gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                    className="gap-1 md:gap-2"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 text-green-400" />
                        <span className="hidden sm:inline">已复制</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span className="hidden sm:inline">复制</span>
                      </>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleFavorite}
                    disabled={!user}
                  >
                    <Heart
                      className={`w-4 h-4 ${
                        isFavorited ? 'fill-red-500 text-red-500' : ''
                      }`}
                    />
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* 内容区域 */}
              <div className="flex-1 overflow-y-auto p-4 md:p-6">
                <div className="prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown
                    components={{
                      // 自定义 Markdown 组件样式
                      h1: ({ children }) => (
                        <h1 className="text-2xl font-bold mb-4 mt-6 first:mt-0">
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-xl font-bold mb-3 mt-5">
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-lg font-bold mb-2 mt-4">
                          {children}
                        </h3>
                      ),
                      p: ({ children }) => (
                        <p className="mb-3 leading-relaxed">
                          {children}
                        </p>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc list-inside mb-3 space-y-1">
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="list-decimal list-inside mb-3 space-y-1">
                          {children}
                        </ol>
                      ),
                      code: ({ children, className }) => {
                        const isInline = !className;
                        return isInline ? (
                          <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-sm font-mono border border-border">
                            {children}
                          </code>
                        ) : (
                          <code className="block p-4 rounded-lg bg-muted text-foreground text-sm font-mono overflow-x-auto border border-border">
                            {children}
                          </code>
                        );
                      },
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-border pl-4 italic text-muted-foreground my-4 bg-muted py-2">
                          {children}
                        </blockquote>
                      ),
                      table: ({ children }) => (
                        <div className="overflow-x-auto my-4">
                          <table className="min-w-full border border-border">
                            {children}
                          </table>
                        </div>
                      ),
                      th: ({ children }) => (
                        <th className="border border-border px-4 py-2 bg-muted font-semibold text-left">
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td className="border border-border px-4 py-2">
                          {children}
                        </td>
                      ),
                    }}
                  >
                    {prompt.content}
                  </ReactMarkdown>
                </div>
              </div>

              {/* 底部信息 */}
              <div className="p-4 md:p-6 border-t border-border text-xs md:text-sm text-muted-foreground">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
                  <div className="flex flex-wrap items-center gap-2 md:gap-4">
                    {prompt.author && <span>作者: {prompt.author}</span>}
                    <span>分类: {prompt.category}</span>
                    <span>权限: {prompt.permission}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:gap-4">
                    <span>创建: {new Date(prompt.createdAt * 1000).toLocaleDateString()}</span>
                    <span>更新: {new Date(prompt.updatedAt * 1000).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

